import { useCallback, useEffect, useMemo, useState } from 'react';
import { CommonProperties } from '../../common/common.interfaces';
import { useUserContext } from '../../user/UserContext';
import { DutyDrawbackExceptionItem, OrderItemsFlexportTableRow } from './flexport-duty-drawback.interfaces';
import { DateTime } from 'luxon';
import RefreshSvg from '../../common/components/svg/RefreshSvg';
import { useLoading } from '../../loading/LoadingContext';
import { apiFetch } from '../../utility/fetch';
import { API_PROD_URL } from '../..';
import CustomError, { CustomErrorProps } from '../../common/components/CustomError';
import { Button, ProgressBar } from 'react-bootstrap';
import './flexport-duty-drawback.css'
import FlexportDutyDrawbackExceptions from './FlexportDutyDrawbackExceptions';
import { notify } from '../../notify/notify';

interface FlexportDutyDrawbackProps extends CommonProperties {}

export default function FlexportDutyDrawback(props: FlexportDutyDrawbackProps) {
  const START_DATE = DateTime.fromObject({month: 6, year: 2020})

  const {userData} = useUserContext()
  const {toggleLoading, changeStep, changeSubStep} = useLoading()
  const [rows, setRows]: [OrderItemsFlexportTableRow[], any] = useState([])
  const [potentialRows, setPotentialRows]: [OrderItemsFlexportTableRow[], any] = useState([])
  const [refresh, setRefresh]: [string, any] = useState(DateTime.now().toLocaleString(DateTime.TIME_WITH_SECONDS))
  const [error, setError]: [CustomErrorProps, any] = useState({visible: false})
  const [progress, setProgress]: [{completed: number, total: number, pending: number, exceptions: number}, any] = useState({completed: 0, total: 0, pending: 0, exceptions: 0})
  const [showExceptions, setShowExceptions] = useState(false)
  const [exceptions, setExceptions]: [DutyDrawbackExceptionItem[], any] = useState(undefined)
  const [exceptionsTitle, setExceptionsTitle] = useState(undefined)
  const [exceptionsRow, setExceptionsRow] = useState(undefined)

  useEffect(() => {
    const load = async function () {
      changeStep('Loading Flexport Duty Drawbacks')
      changeSubStep('Getting data from Azure.')
      toggleLoading(true)

      const tableResponse = await apiFetch<OrderItemsFlexportTableRow[]>({
        endpoint: 'azureTables',
        method: 'GET',
        accessToken: userData.accessToken,
        baseUrlOverride: API_PROD_URL,
        params: [
          {key: 'table', value: 'orderItemsFlexport'}
        ]
      })
      if(!tableResponse.ok){
        setError({
          closeError: () => {setError({visible: false})},
          content: JSON.stringify(tableResponse.body),
          description: 'Failed to get Flexport Duty Drawback records from Azure.',
          title: 'Flexport Duty Drawback Error',
          visible: true
        } as CustomErrorProps)
        toggleLoading(false)
        return
      }
      const sortedExisting = tableResponse.body.sort((a, b) => {
        if (a.year !== b.year) {
          return a.year - b.year;
        }
        return a.week - b.week;
      });
      
      setRows(sortedExisting)

      /** Build potential new rows */
      const existingRowSet = new Set(
        sortedExisting.map((row) => `${row.year}-${row.week}`)
      );

      const potential: OrderItemsFlexportTableRow[] = [];

      let current = START_DATE.startOf('week'); // ensure starting at beginning of week
      const now = DateTime.now().startOf('week');

      while (current <= now) {
        const year = current.weekYear;
        const week = current.weekNumber;

        // Skip the current week
        if (year === now.weekYear && week === now.weekNumber) {
          break;
        }

        const key = `${year}-${week}`;
        if (!existingRowSet.has(key)) {
          potential.push({
            partitionKey: `${year}`,
            rowKey: `${week}`, // use week as the unique row identifier
            year,
            week,
          });
        }

        // Move to next week
        current = current.plus({ weeks: 1 });
      }

      const sortedPotential = potential.sort((a, b) => {
        if (a.year !== b.year) {
          return a.year - b.year;
        }
        return a.week - b.week;
      });
      setPotentialRows(sortedPotential);

      const incomplete = potential.length
      const completedRows = tableResponse.body.filter(
        (row) =>
          row.exportStatus?.trim().toLowerCase() === 'complete'
      );
      const pendingRows = tableResponse.body.filter(
        (row) =>
          row.exportStatus?.trim().toLowerCase() === 'pending' ||
          row.exportStatus?.trim().toLowerCase() === 'in-progress'
      );
      const exceptionRows = tableResponse.body.filter(
        (row) => 
          row.exportStatus?.trim().toLowerCase() === 'exceptions'
      )

      
      const completedCount = completedRows.length;
      const pendingCount = pendingRows.length;
      const exceptionsCount = exceptionRows.length

      const completePercent = (completedCount / (incomplete + tableResponse.body.length)) * 100;
      const pendingPercent = (pendingCount / (incomplete + tableResponse.body.length)) * 100;
      const exceptionsPercent = (exceptionsCount / (incomplete + tableResponse.body.length)) * 100;
      
      setProgress({
        completed: completePercent,
        total: incomplete + tableResponse.body.length,
        pending: pendingPercent,
        exceptions: exceptionsPercent
      });
          
      toggleLoading(false)
    }
    load()
  }, [refresh])

  const startExportClicked = useCallback(async (e) => {
    const {year, week} = e.currentTarget.dataset
    const date = DateTime.fromObject({weekNumber: week, weekYear: year}).toFormat('WW yyyy')
    if(!confirm(`Are you sure you want to start the export process for Week ${date}?`)) return

    changeStep(`Starting Export for ${date}`)
    changeSubStep('Adding record for processing.')
    toggleLoading(true)
    const record: OrderItemsFlexportTableRow = {
      partitionKey: year,
      rowKey: week,
      year: year,
      week: week,
      exportPath: '',
      exportStatus: 'pending',
      exportTimestamp: '',
      jsonPath: ''
    }
    const tableResponse = await apiFetch({
      endpoint: 'azureTables',
      method: 'POST',
      accessToken: userData.accessToken,
      baseUrlOverride: API_PROD_URL,
      body: JSON.stringify({
        table: 'orderItemsFlexport',
        records: [record]
      })
    })
    if(!tableResponse.ok){
      setError({
        closeError: () => {setError({visible: false})},
        content: JSON.stringify(tableResponse.body),
        description: `Failed to start export process for ${date}.`,
        title: 'Flexport Duty Drawback Error',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }
    toggleLoading(false)
    setRefresh(DateTime.now().toLocaleString(DateTime.TIME_WITH_SECONDS))
  }, [userData])

  const rerunExportClicked = useCallback(async (e) => {
    const {year, week} = e.currentTarget.dataset
    const date = DateTime.fromObject({weekNumber: week, weekYear: year}).toFormat('WW yyyy')
    if(!confirm(`Are you sure you want to rerun the export process for Week ${date}?`)) return

    changeStep(`Rerunning Export for ${date}`)
    changeSubStep('Updating record for processing.')
    toggleLoading(true)
    const record: OrderItemsFlexportTableRow = {
      partitionKey: year,
      rowKey: week,
      year: year,
      week: week,
      exportPath: '',
      exportStatus: 'pending',
      exportTimestamp: '',
      jsonPath: ''
    }
    const tableResponse = await apiFetch({
      endpoint: 'azureTables',
      method: 'POST',
      accessToken: userData.accessToken,
      baseUrlOverride: API_PROD_URL,
      body: JSON.stringify({
        table: 'orderItemsFlexport',
        records: [record]
      })
    })
    if(!tableResponse.ok){
      setError({
        closeError: () => {setError({visible: false})},
        content: JSON.stringify(tableResponse.body),
        description: `Failed to rerun export process for ${date}.`,
        title: 'Flexport Duty Drawback Error',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }
    toggleLoading(false)
    setRefresh(DateTime.now().toLocaleString(DateTime.TIME_WITH_SECONDS))
  }, [userData])

  const handleShowExceptionsClicked = useCallback(async (e) => {
    const {year, week} = e.currentTarget.dataset
    const date = DateTime.fromObject({weekNumber: week, weekYear: year}).toFormat('MMM yyyy')
    const row = rows.find(r => r.partitionKey === `${year}` && r.rowKey === `${week}`)
    if(!row){
      notify('error', `Could not find row for ${date}!`)
      return
    }

    changeStep(`Loading Exceptions for ${date}`)
    changeSubStep('Getting data from.')
    toggleLoading(true)

    const exceptionResponse = await apiFetch({
      endpoint: 'blob',
      method: 'GET',
      accessToken: userData.accessToken,
      baseUrlOverride: API_PROD_URL,
      params: [
        {key: 'location', value: 'dataservices'},
        {key: 'container', value: 'flexport'},
        {key: 'blobname', value: row.exceptionsPath}
      ]
    })
    if(!exceptionResponse.ok){
      setError({
        closeError: () => setError({visible: false}),
        content: JSON.stringify(exceptionResponse.body),
        description: `Failed to load exceptions for ${date} from ${row.exceptionsPath} in the Flexport blob container.`,
        title: `Load Exceptions Failed`,
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }
    setExceptions(exceptionResponse.body)
    setExceptionsRow(row)
    toggleLoading(false)
    setExceptionsTitle(`Order Item Exceptions for ${date} [ ${row.exceptionsCount} ]`)
    setShowExceptions(true)
  }, [rows])

  const startAllClicked = useCallback(async (e) => {
    if(!confirm(`Are you sure you want to run the export process for ${potentialRows.length} weeks?`)) return

    changeStep(`Saving Export Records`)
    changeSubStep(`Updating ${potentialRows.length} record(s) for processing.`)
    toggleLoading(true)
    const records: OrderItemsFlexportTableRow[] = potentialRows.map((pr) => {
      return {
        partitionKey: `${pr.year}`,
        rowKey: `${pr.week}`,
        year: pr.year,
        week: pr.week,
        exportPath: '',
        exportStatus: 'pending',
        exportTimestamp: '',
        jsonPath: ''
      }
    })

    const tableResponse = await apiFetch({
      endpoint: 'azureTables',
      method: 'POST',
      accessToken: userData.accessToken,
      baseUrlOverride: API_PROD_URL,
      body: JSON.stringify({
        table: 'orderItemsFlexport',
        records: records
      })
    })
    if(!tableResponse.ok){
      setError({
        closeError: () => {setError({visible: false})},
        content: JSON.stringify(tableResponse.body),
        description: `Failed to rerun export process.`,
        title: 'Flexport Duty Drawback Error',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }
    toggleLoading(false)
    setRefresh(DateTime.now().toLocaleString(DateTime.TIME_WITH_SECONDS))
  }, [potentialRows])

  const view = useMemo(() => {
    return <div>
      <FlexportDutyDrawbackExceptions row={exceptionsRow} userData={userData} closeModal={() => {setShowExceptions(false)}} modalVisible={showExceptions} title={exceptionsTitle} exceptions={exceptions} />
      <div id='top'></div>
      <CustomError 
        closeError={() => {setError({visible: false})}}
        content={error?.content}
        description={error?.description}
        title={error?.title}
        visible={error?.visible}
      />
      <div className='beacon-card'>
        <span style={{cursor: 'pointer'}} title='Refresh the table.' onClick={() => {setRefresh(DateTime.now().toLocaleString(DateTime.TIME_WITH_SECONDS))}}><RefreshSvg height={20} /></span><span className='beacon-label'> as of {refresh}</span>
        <span className='beacon-label'> with {rows.length} week(s) loaded.</span>
        <div style={{float: 'right'}}>
          <Button variant='secondary' onClick={startAllClicked}>START ALL</Button>
        </div>
      </div>
      <div className='beacon-label' style={{textAlign: 'center'}}>
        {progress.completed.toFixed(2)}% - {rows.filter(r => r.exportStatus === 'complete').length}/{potentialRows.length + rows.length} WEEKS COMPLETE
      </div>
      <ProgressBar>
        <ProgressBar animated variant='success' now={progress.completed}/>
        <ProgressBar animated variant='warning' now={progress.pending}/>
        <ProgressBar animated variant='danger' now={progress.exceptions}/>
      </ProgressBar>
      <div className="beacon-card">
        <div style={{textAlign: 'center'}}>
          <span className='beacon-label'>
            {rows.filter(r => r.exportStatus === 'complete').length} COMPLETE
          </span>&nbsp;&nbsp;
          <span className='beacon-label'>
            {rows.filter(r => r.exportStatus === 'in-progress').length} IN PROGRESS
          </span>
        </div>
        <table className='flexport-table'>
          <thead>
            <tr className="sticky-row-2">
              <th></th>
              <th>EXPORT WEEK</th>
              <th>EXPORT STATUS</th>
              <th>LAST EXPORT</th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {[...rows, ...potentialRows].map((row, index) => (
              <tr key={index}>
                <td><span className='beacon-label'>{index + 1}</span></td>
                <td><span>{DateTime.fromObject({weekYear: row.year, weekNumber: row.week}).toFormat('WW yyyy (MMM yy)')}</span></td>
                <td>{row.exportStatus || 'N/A'}</td>
                <td>{row.exportTimestamp ? DateTime.fromISO(row.exportTimestamp).setZone('America/Chicago').toLocaleString(DateTime.DATETIME_SHORT) : 'N/A'}</td>
                <td>
                  {!row.exportStatus ? (
                    <Button data-year={row.year} data-week={row.week} onClick={startExportClicked} title="Starts the Flexport Duty Drawback export process for this week and year." variant="secondary">
                      START EXPORT
                    </Button>
                  ) : row.exportStatus === 'complete' || row.exportStatus === 'exceptions' ? (
                    <>
                      <Button data-year={row.year} data-week={row.week} onClick={rerunExportClicked} title="Re-run the Flexport Duty Drawback export process for this week and year." variant="secondary">
                        RERUN EXPORT
                      </Button>&nbsp;&nbsp;
                      {row.exportStatus === 'exceptions' &&
                        <Button data-year={row.year} data-week={row.week} onClick={handleShowExceptionsClicked} title="Manage exceptions that occurred for order items." variant="warning">
                          EXCEPTIONS
                        </Button>
                      }
                    </>
                  ) : (
                    <span className='beacon-label'>
                      {row.exportStatus === 'in-progress' ? <>Currently processing...</> : <>In queue...</>}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

    </div>
  }, [refresh, rows, progress, potentialRows, showExceptions, exceptionsTitle, userData, exceptionsRow])

  return view
}